import { useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { N8N_WEBHOOKS } from "../constants";
import { securelyTriggerN8n } from "../lib/n8n";

// =============================================================================
// useMessagePatterns
// =============================================================================
// PURPOSE: Manage message patterns for ManyChat detection and classification.
//
// FEATURES:
// - Fetch patterns with optional classification filter (direct Supabase read)
// - Refresh patterns (N8N webhook → regenerate from ghl_messages)
// - Classify individual patterns (N8N webhook → manychat/human_script/unclassified)
//
// ARCHITECTURE:
// - READS: Direct Supabase queries (allowed)
// - WRITES: Via N8N webhook (required pattern for all writes)
//
// USAGE:
// const { patterns, isLoading, fetchPatterns, refreshPatterns, classifyPattern } = useMessagePatterns({
//   organizationId,
//   classificationFilter: "unclassified"
// });
// =============================================================================

export interface MessagePattern {
  id: string;
  organization_id: string;
  message_text: string;
  message_hash: string;
  occurrence_count: number;
  classification: "manychat" | "human_script" | "unclassified";
  classified_at: string | null;
  classified_by: string | null;
  sample_conversation_ids: string[];
  first_seen_at: string;
  last_seen_at: string;
}

export interface PatternSample {
  conversation_id: string;
  contact_name: string;
  message_text: string;
  sent_at: string;
  setter_name: string;
}

export interface PatternDelayMode {
  mode_minutes: number;  // Exact delay in minutes (the actual mode value)
  mode_count: number;    // How many times this exact delay occurred
  total_count: number;   // Total samples analyzed
  confidence: number;    // mode_count / total_count
}

interface UseMessagePatternsOptions {
  organizationId: string;
  classificationFilter?: "all" | "manychat" | "human_script" | "unclassified";
}

export function useMessagePatterns({
  organizationId,
  classificationFilter = "all",
}: UseMessagePatternsOptions) {
  const [patterns, setPatterns] = useState<MessagePattern[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // READ: Direct Supabase query (allowed)
  const fetchPatterns = useCallback(async () => {
    if (!organizationId || !supabase) return;
    setIsLoading(true);
    setError(null);

    try {
      let query = supabase
        .from("message_patterns")
        .select("*")
        .eq("organization_id", organizationId)
        .order("occurrence_count", { ascending: false });

      if (classificationFilter !== "all") {
        query = query.eq("classification", classificationFilter);
      }

      const { data, error: queryError } = await query;
      if (queryError) throw queryError;
      setPatterns(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch patterns");
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, classificationFilter]);

  // WRITE: Via N8N webhook (required)
  const refreshPatterns = useCallback(async () => {
    if (!organizationId) return;
    setIsRefreshing(true);
    setError(null);

    try {
      await securelyTriggerN8n(N8N_WEBHOOKS.ALTARA_MESSAGE_PATTERN, {
        action: "refresh",
        organization_id: organizationId,
      });

      await fetchPatterns(); // Reload after refresh
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh patterns");
    } finally {
      setIsRefreshing(false);
    }
  }, [organizationId, fetchPatterns]);

  // WRITE: Via N8N webhook (required)
  const classifyPattern = useCallback(
    async (patternId: string, classification: "manychat" | "human_script" | "unclassified") => {
      // Optimistic update first for instant UI feedback
      setPatterns((prev) =>
        prev.map((p) =>
          p.id === patternId ? { ...p, classification, classified_at: new Date().toISOString() } : p
        )
      );

      try {
        await securelyTriggerN8n(N8N_WEBHOOKS.ALTARA_MESSAGE_PATTERN, {
          action: "classify",
          pattern_id: patternId,
          classification: classification,
        });

        return true;
      } catch (err) {
        // Revert optimistic update on failure
        await fetchPatterns();
        setError(err instanceof Error ? err.message : "Failed to classify pattern");
        return false;
      }
    },
    [fetchPatterns]
  );

  // Fetch sample messages for a specific pattern
  const fetchSamples = useCallback(
    async (patternId: string, limit = 12): Promise<PatternSample[]> => {
      if (!supabase) return [];

      const { data, error: queryError } = await supabase.rpc("get_pattern_sample_messages", {
        p_pattern_id: patternId,
        p_limit: limit,
      });

      if (queryError) {
        console.error("Failed to fetch pattern samples:", queryError);
        return [];
      }

      return data || [];
    },
    []
  );

  // Fetch delay mode for a specific pattern (used to detect ManyChat automation)
  const fetchDelayMode = useCallback(
    async (patternId: string): Promise<PatternDelayMode | null> => {
      if (!supabase) return null;

      const { data, error: queryError } = await supabase.rpc("get_pattern_delay_mode", {
        p_pattern_id: patternId,
      });

      if (queryError) {
        console.error("Failed to fetch pattern delay mode:", queryError);
        return null;
      }

      // RPC returns array, get first row
      return data?.[0] || null;
    },
    []
  );

  return {
    patterns,
    isLoading,
    isRefreshing,
    error,
    fetchPatterns,
    refreshPatterns,
    classifyPattern,
    fetchSamples,
    fetchDelayMode,
  };
}
